import {Button} from 'platform/components';
import {Clickable, HStack, Icon, Text, ThemeIconKey} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';

import {KeyboardEvent} from 'react';

import {TelephonyCall} from '@dms/api/settings';
import i18n from '@dms/i18n';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

interface CallCustomerActionsProps extends RequiredTestIdProps {
  call: TelephonyCall;
  /** On the dark call bar the actions are white text, so they sit flush with the bar. */
  isOnDarkSurface?: boolean;
  onOpenCustomer: (customerId: string) => void;
  onCreateCustomer: VoidFunction;
  onFindCustomer: VoidFunction;
}

interface CallCustomerActionProps extends RequiredTestIdProps {
  title: string;
  icon: ThemeIconKey;
  isOnDarkSurface: boolean;
  onClick: VoidFunction;
}

function CallCustomerAction(props: CallCustomerActionProps) {
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      props.onClick();
    }
  };

  return props.isOnDarkSurface ? (
    <Clickable
      role="button"
      tabIndex={0}
      onClick={props.onClick}
      onKeyDown={handleKeyDown}
      data-testid={props['data-testid']}
    >
      <HStack spacing={1} align="center">
        <Icon value={props.icon} size={4} color="text.white" />
        <Text size="small" color="white" noWrap>
          {props.title}
        </Text>
      </HStack>
    </Clickable>
  ) : (
    <Button
      variant="secondary"
      size="small"
      title={props.title}
      onClick={props.onClick}
      data-testid={props['data-testid']}
    />
  );
}

/**
 * The way from a call to its customer: open the matched customer, or, for an inbound number that
 * matches nobody, create or look up the customer. A withheld number offers nothing.
 */
export function CallCustomerActions(props: CallCustomerActionsProps) {
  const isOnDarkSurface = props.isOnDarkSurface ?? false;

  return match(props.call)
    .with({customerId: Pattern.string}, (call) => (
      <CallCustomerAction
        title={i18n.t('page.telephony.indicator.openCustomer')}
        icon="action/open_in_new"
        isOnDarkSurface={isOnDarkSurface}
        onClick={() => props.onOpenCustomer(call.customerId)}
        data-testid={suffixTestId('openCustomer', props)}
      />
    ))
    .with({customerPhoneNumber: Pattern.string}, () => (
      <HStack spacing={isOnDarkSurface ? 4 : 2} align="center">
        <CallCustomerAction
          title={i18n.t('entity.customer.actions.addCustomer')}
          icon="content/add"
          isOnDarkSurface={isOnDarkSurface}
          onClick={props.onCreateCustomer}
          data-testid={suffixTestId('createCustomer', props)}
        />
        <CallCustomerAction
          title={i18n.t('page.telephony.indicator.findCustomer')}
          icon="action/search"
          isOnDarkSurface={isOnDarkSurface}
          onClick={props.onFindCustomer}
          data-testid={suffixTestId('findCustomer', props)}
        />
      </HStack>
    ))
    .otherwise(() => null);
}
